// packages/auth/src/administration-access.ts
import type { Role } from './role.js'
import type { Scope } from './scope.js'

/**
 * ★ PHASE 6 — who may read and administer the three reference-data
 * administration surfaces Tasks 18/19 add: class of wards (Annex A 26),
 * sub-system information (Annex A 27) and RA/PA mapping (Annex A 28).
 *
 * ⚠ **All three name exactly one actor, without exception: "User (PSD User)".**
 * Unlike `reference-access.ts`'s `REFERENCE_READ_ROLES` — which widens several
 * of the nine pro-ration parameters to the role that actually consumes each one
 * (an HSP reading its own pegged rate, a Payment Agency reading account codes)
 * — nothing in stories 26, 27 or 28's own text names a second reader. Read and
 * administer are therefore the SAME set for all three, which is why this file
 * is three pairs of one-line functions rather than a `Record<Resource,
 * ReadonlySet<Role>>` the way `reference-access.ts` needs for nine parameters
 * with nine different answers.
 *
 * Kept as SIX separate exported functions rather than one shared
 * `mayAdministerPsdCatalogue(scope)` reused three times: each story is its own
 * Annex A obligation and may widen independently later (the RA/PA mapping is
 * the most likely candidate — a Payment Agency reading only ITS OWN mapping is
 * a real future need story 28's text does not yet ask for) without touching
 * the other two's authorisation at all.
 */
const PSD_ONLY: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/**
 * ★★ ADR 0011 — PSD may READ the account directory. Nothing here lets it write.
 *
 * ══ WHY THERE IS NO `mayAdministerAccountDirectory` BESIDE THIS ══
 *
 * Every other pair in this file is `mayRead…` / `mayAdminister…`, and the
 * asymmetry here is the decision rather than an omission. Annex A stories 52–59
 * describe an API **CAM consumes** (P2C3 §1.1.1(b)), and the obligation that a
 * user's access can be removed promptly is CAM's. Nothing in the received pack
 * says a PSD officer disables an account from a screen, and two writers to
 * account state raises a question the pack does not answer either: which system
 * is the system of record when they disagree.
 *
 * So the writes stay on `/api/cam/*` behind `requireCamAgent`, a MACHINE
 * credential — and a PSD session, however senior, does not reach them. ADR 0011
 * carries the full argument and is where an Authority answer would land.
 *
 * ⚠ **This predicate must never be used to gate a mutation.** If a write is ever
 * authorised for PSD, it gets its OWN predicate in the same change that answers
 * the system-of-record question, so the read grant cannot silently become a
 * write grant by reuse.
 */
export function mayReadAccountDirectory(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★★★ WAVE 12 (P2) — Annex A story 14's own title: the CENTRAL CONFIGURATION
 * MODULE. Which identity providers this deployment offers, and how a verified
 * claim resolves to a local account.
 *
 * PSD only, and this one is not a formality. The catalogue decides **which doors
 * exist to sign in through** and **which claim identifies an officer** — so a
 * writer here can, in principle, point the estate at a different issuer or change
 * whose token maps to whose account. That is the most consequential
 * administration surface in the product, and it is why the WRITE predicate is
 * separate from the READ one even though both resolve to PSD today: a future
 * widening of the read (an auditor who may see the configuration) must not
 * silently become a widening of the write.
 *
 * ⚠ **Neither predicate gates a SECRET**, because no secret is in the catalogue.
 * The client secret stays in the deployment environment — see
 * `061_identity_provider_config.sql`'s header for the three reasons, of which the
 * load-bearing one is that this table is exported to PDF/Excel by the same shared
 * report kernel every other catalogue uses.
 */
export function mayReadIdentityProviders(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerIdentityProviders(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/** Annex A story 26 — "User (PSD User)" without exception. */
export function mayReadWardClasses(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerWardClasses(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/** Annex A story 27 — "User (PSD User)" without exception. */
export function mayReadSubsystems(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerSubsystems(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * Annex A story 28 — "User (PSD User)" without exception.
 *
 * ⚠ Not widened to `PAYMENT_USER`/`RECOVERY_USER` even though each is a
 * plausible future reader of ITS OWN mapping (the same shape `PEGGED_WARD_RATE`
 * grants `HSP_USER` in `reference-access.ts`) — story 28's own text names only
 * PSD, and widening this without a stated need would be inventing an
 * authorisation policy the backlog does not ask for.
 */
export function mayReadAgencyMappings(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerAgencyMappings(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 20 — Annex A story 31: "Medical Scheme Code Management",
 * "User (PSD User)" without exception, same posture as the three above.
 */
export function mayReadMedicalSchemeCodes(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerMedicalSchemeCodes(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 21 — Annex A stories 29/30: "HSP Group Information
 * Management" and "HSP Information Tagged to HSP Group", both "User (PSD
 * User)" without exception. One pair of functions for the group catalogue,
 * one for its membership — the two stay independently widenable, the same
 * reasoning `mayReadAgencyMappings`'s own header gives for keeping story 28
 * separate from stories 26/27.
 */
export function mayReadHspGroups(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerHspGroups(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayReadHspGroupMemberships(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerHspGroupMemberships(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 22 — Annex A story 36: "MBIM Record Management".
 *
 * ⚠ **The one Phase 6 administration surface NOT restricted to PSD_USER.**
 * Story 36 names its own actor as "User (MBIM User)" — `023_mbim_record.sql`'s
 * own header already recorded this ("story 36 ... MBIM_USER acting on PRS2
 * directly") before this task built the write path. Restricted to MBIM_USER
 * alone, never widened to PSD_USER: the same "no reader beyond who the story
 * itself names" discipline every other function in this file follows, applied
 * to a different single role.
 */
const MBIM_ONLY: ReadonlySet<Role> = new Set<Role>(['MBIM_USER'])

export function mayReadMbimRecords(scope: Scope): boolean {
  return MBIM_ONLY.has(scope.role)
}
export function mayAdministerMbimRecords(scope: Scope): boolean {
  return MBIM_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 23 — Annex A story 32: "Exclusion Lists for eCS Card and
 * Foreign ID", "User (PSD User)" without exception — back to the PSD-only
 * posture stories 26-31 share (story 32 names no other actor).
 *
 * ⚠ This is an authorisation gate, not the control that protects the personal
 * data this table holds — masking (`exclusion-list-repository.ts`) and
 * audit-fingerprinting (`audit-resource.ts`'s `attributeExclusionListEntry`)
 * do that work regardless of who is asking. See `040_exclusion_list.sql`'s
 * own header (A-40, SECURITY.md S-030).
 */
export function mayReadExclusionList(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerExclusionList(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 24 — Annex A story 33: "Email Template Management", "User
 * (PSD User)" without exception. ⚠ This is TEMPLATE ADMINISTRATION ONLY — no
 * delivery mechanism existed anywhere in this codebase — one does now, and
 * whether it reaches anybody is `notificationDelivery`, a deployment fact. See
 * `041_email_template.sql`'s own header.
 */
export function mayReadEmailTemplates(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerEmailTemplates(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/** ★ PHASE 6, TASK 25 — Annex A story 34: "Announcement Management", "User (PSD User)" without exception. */
export function mayReadAnnouncements(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerAnnouncements(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 6, TASK 26 — Annex A story 35: "Record Management for Special
 * Agencies/Cases".
 *
 * ⚠ The SECOND Phase 6 administration surface (after story 36's MBIM
 * records) not restricted to PSD_USER — story 35 names its own actor as
 * "User (Entitlement User)". Restricted to ENTITLEMENT_USER alone, never
 * widened to PSD_USER, the same "no reader beyond who the story itself
 * names" discipline every function in this file follows.
 */
const ENTITLEMENT_ONLY: ReadonlySet<Role> = new Set<Role>(['ENTITLEMENT_USER'])

export function mayReadSpecialAgencyCases(scope: Scope): boolean {
  return ENTITLEMENT_ONLY.has(scope.role)
}
export function mayAdministerSpecialAgencyCases(scope: Scope): boolean {
  return ENTITLEMENT_ONLY.has(scope.role)
}

/**
 * ★ PHASE 8 — Annex A 52-59: the eight CAM-consumed REST endpoints
 * (P2C3 §1.1.1(b)).
 *
 * ⚠ **Direction matters, and this function is the whole of the difference.**
 * Every OTHER function in this file gates a HUMAN role reading or changing a
 * catalogue. CAM is not a human role calling INTO a PRS2 screen — it is
 * GovTech's own system calling this Solution's endpoints, per P2C3
 * §1.1.1(b): *"CAM will consume the REST API interfaces from the Solution"*.
 * So the only caller ever authorised here is one `services/gateway/src
 * /cam-auth.ts`'s `requireCamAgent` has already verified against the shared
 * CAM credential and stamped `role: 'CAM_AGENT'` onto — no PSD_USER session,
 * however privileged, may reach these routes by any other path. A caller with
 * a valid PRS2 cookie and no CAM credential is refused exactly like one with
 * neither.
 */
const CAM_AGENT_ONLY: ReadonlySet<Role> = new Set<Role>(['CAM_AGENT'])

export function mayActAsCamAgent(scope: Scope): boolean {
  return CAM_AGENT_ONLY.has(scope.role)
}

/**
 * ★ PHASE 10 — Annex A story 50: "Email Notification for eCS New Entitlement
 * Creation", "User (PSD User)" without exception — the same posture stories
 * 26-34 already carry. Covers reading/administering `app.notification_rule`,
 * reading `app.notification_log`, and firing the trigger endpoint that
 * records a notifiable event — all three are PSD's own configuration and
 * monitoring surface over a mechanism that sends nothing (see
 * `050_notification.sql`'s own header).
 */
export function mayReadNotificationRules(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayAdministerNotificationRules(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayReadNotificationLog(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}
export function mayTriggerNotificationEvent(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★ PHASE 11 — Annex A story 48: "WOG ITSM Helpdesk Ticketing System
 * Integration". Raising a ticket is open to every role story 48 itself
 * names (all eight human roles — see `general-access.ts`'s own
 * `HUMAN_ROLES`), so that check lives there, not here. Managing the local
 * ticket queue (seeing every ticket regardless of who raised it, and moving
 * one through its lifecycle) is PSD's own operator surface, the same
 * posture every other administration catalogue in this file already
 * carries.
 */
export function mayManageSupportTickets(scope: Scope): boolean {
  return PSD_ONLY.has(scope.role)
}

/**
 * ★★★ ADR 0013 — USER ACCOUNT ADMINISTRATION: who may sign in, as which role, with which scope.
 *
 * `SYSTEM_ADMIN` only, and for BOTH predicates — not PSD. `mayReadAccountDirectory` above stays
 * PSD-only and read-only (ADR 0011: CAM is the system of record for account state); this is a
 * different, LOCAL surface for deployments where PRS2 itself must create the accounts, and it is
 * deliberately held by a role that has no bill, approval or reference-data capability, so the
 * person who can grant access cannot also use the access they grant.
 *
 * ⚠ **Two predicates, one actor today.** The read predicate must never gate a mutation: a future
 * auditor who may SEE the account list (a read widening) must not silently gain the write.
 */
export function mayReadUserAccounts(scope: Scope): boolean {
  return scope.role === 'SYSTEM_ADMIN'
}

export function mayAdministerUserAccounts(scope: Scope): boolean {
  return scope.role === 'SYSTEM_ADMIN'
}
